import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react'
import type { ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import {
  signIn as supabaseSignIn,
  signUp as supabaseSignUp,
  signOut as supabaseSignOut,
  getSession,
  onAuthStateChange,
} from '@/lib/supabase'

interface AuthContextValue {
  user: User | null
  session: Session | null
  isLoading: boolean
  isAuthenticated: boolean
  signIn: (
    email: string,
    password: string,
    captchaToken?: string
  ) => Promise<{ error: string | null }>
  signUp: (
    email: string,
    password: string,
    orgName: string,
    captchaToken?: string
  ) => Promise<{ error: string | null; confirmationRequired: boolean }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const userIdRef = useRef<string | null>(null)

  // Initialize auth state on mount
  useEffect(() => {
    let mounted = true

    async function initAuth() {
      try {
        const { session: currentSession } = await getSession()
        if (mounted) {
          setSession(currentSession)
          const newUserId = currentSession?.user?.id ?? null
          if (userIdRef.current !== newUserId) {
            userIdRef.current = newUserId
            setUser(currentSession?.user ?? null)
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    initAuth()

    // Subscribe to auth state changes
    const subscription = onAuthStateChange((_event, newSession) => {
      if (mounted) {
        setSession(newSession)
        const newUserId = newSession?.user?.id ?? null
        if (userIdRef.current !== newUserId) {
          userIdRef.current = newUserId
          setUser(newSession?.user ?? null)
        }
        setIsLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signIn = useCallback(
    async (
      email: string,
      password: string,
      captchaToken?: string
    ): Promise<{ error: string | null }> => {
      setIsLoading(true)
      try {
        const result = await supabaseSignIn(email, password, captchaToken)
        if (result.error) {
          return { error: result.error.message }
        }
        return { error: null }
      } catch (error) {
        console.error('Sign in error:', error)
        return { error: 'An unexpected error occurred' }
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      orgName: string,
      captchaToken?: string
    ): Promise<{ error: string | null; confirmationRequired: boolean }> => {
      setIsLoading(true)
      try {
        const result = await supabaseSignUp(
          email,
          password,
          {
            orgName,
            displayName: email.split('@')[0],
          },
          captchaToken
        )
        if (result.error) {
          return { error: result.error.message, confirmationRequired: false }
        }
        return { error: null, confirmationRequired: result.confirmationRequired }
      } catch (error) {
        console.error('Sign up error:', error)
        return {
          error: 'An unexpected error occurred',
          confirmationRequired: false,
        }
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const signOut = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    try {
      await supabaseSignOut()
    } catch (error) {
      console.error('Sign out error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const value: AuthContextValue = {
    user,
    session,
    isLoading,
    isAuthenticated: !!session,
    signIn,
    signUp,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
